import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Paperclip, CheckCircle2, CornerUpLeft, Hand, Undo2, Upload, FileStack, CalendarClock, Trash2, PackageCheck } from "lucide-react";
import { api, errMsg, fmtDate, fmtDay, isManager } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, btnPrimary, btnGhost, inputCls } from "../components/ui-kit";
import { StatusBadge, RoleBadge, TypeBadge, ClassLinkedBadge } from "../components/Badges";
import { Timeline } from "../components/Timeline";
import { FeedbackComposer, FeedbackToAddress } from "../components/Feedback";
import { VoiceRecorder, feedbackForm } from "../components/Voice";
import { StepOutputs, AttachmentItem } from "../components/StepOutputs";

const Stepper = ({ task }) => (
  <ol className="flex gap-2 overflow-x-auto pb-2" data-testid="task-stepper">
    {task.steps.map((s, i) => {
      const skipped = i < (task.start_step || 0);
      const done = !skipped && (task.status === "completed" || i < task.current_step);
      const cur = task.status !== "completed" && i === task.current_step;
      return (
        <li key={s.id} data-testid={`stepper-step-${i}`} className={`min-w-[150px] flex-1 rounded-lg border p-3 transition-colors ${cur ? "border-[#FF6B00] bg-[#FF6B00]/10" : done ? "border-emerald-500/30 bg-emerald-500/5" : skipped ? "border-dashed border-slate-600 bg-[#141721]" : "border-[#282E3D]"}`}>
          <div className={`font-mono text-[10px] ${cur ? "text-[#FF6B00]" : done ? "text-emerald-400" : skipped ? "text-slate-400" : "text-slate-600"}`}>{skipped ? "DONE OUTSIDE APP" : done ? "DONE" : cur ? "CURRENT" : "UPCOMING"} · {i + 1}</div>
          <div className="mt-1 truncate text-sm font-medium">{s.name}</div>
          <div className="mt-2 flex items-center gap-2"><RoleBadge role={s.role} />{task.attachments.some((a) => a.kind === "output" && a.step_index === i) && <PackageCheck className="h-3.5 w-3.5 text-emerald-400" title="Has an output file" data-testid={`stepper-output-icon-${i}`} />}</div>
        </li>
      );
    })}
  </ol>
);

const AnnotatedFiles = ({ task, user, onChange }) => {
  const files = task.attachments.filter((a) => a.annotated);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  if (!task.class_linked) return null;
  const canUpload = task.status !== "completed" && (isManager(user) || user.roles.includes("faculty"));
  const upload = async (e) => {
    e.preventDefault();
    if (!picked.length) return toast.error("Choose at least one PDF, PPT or Word file");
    const fd = new FormData();
    picked.forEach((f) => fd.append("files", f));
    fd.append("annotated", "true");
    setBusy(true);
    try {
      onChange((await api.post(`/tasks/${task.id}/attachments`, fd)).data);
      setPicked([]); ref.current.value = "";
      toast.success(`${picked.length} annotated file${picked.length > 1 ? "s" : ""} uploaded`);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel className="border-purple-500/30 p-6" data-testid="annotated-files-panel">
      <div className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><FileStack className="h-4 w-4 text-purple-300" />Batch-annotated files <span className="font-mono text-xs text-slate-500" data-testid="annotated-files-count">{files.length}</span></div>
      <p className="mb-4 text-xs text-slate-500">Uploaded by faculty, a manager or the super admin, and available to everyone working on this task.</p>
      <div className="space-y-2">
        {!files.length && <p className="text-sm text-slate-500">Not uploaded yet.</p>}
        {files.map((a) => <AttachmentItem key={a.id} task={task} a={a} />)}
      </div>
      {canUpload && (
        <form onSubmit={upload} className="mt-5 space-y-2 border-t border-[#282E3D] pt-5" data-testid="annotated-upload-form">
          <label className={`${btnGhost} w-full cursor-pointer justify-start`}>
            <Upload className="h-4 w-4" /><span className="truncate" data-testid="annotated-upload-selected">{picked.length ? (picked.length === 1 ? picked[0].name : `${picked.length} files selected`) : "Choose PDF / PPT / Word files (max 25MB each)"}</span>
            <input ref={ref} type="file" multiple accept=".pdf,.ppt,.pptx,.doc,.docx" className="hidden" data-testid="annotated-upload-input" onChange={(e) => setPicked(Array.from(e.target.files))} />
          </label>
          <button data-testid="annotated-upload-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Uploading…" : "Upload annotated files"}</button>
        </form>
      )}
    </Panel>
  );
};

const Attachments = ({ task, canEdit, onChange }) => {
  const [link, setLink] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const submit = async (e) => {
    e.preventDefault();
    if (!files.length && !link.trim()) return toast.error("Add a file or a link");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("link", link);
    fd.append("note", note);
    setBusy(true);
    try {
      onChange((await api.post(`/tasks/${task.id}/attachments`, fd)).data);
      setLink(""); setNote(""); setFiles([]); ref.current.value = "";
      toast.success(files.length > 1 ? `${files.length} files uploaded` : "Attachment added");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const others = task.attachments.filter((a) => !a.annotated && a.kind !== "output");
  return (
    <Panel className="p-6">
      <div className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Paperclip className="h-4 w-4 text-[#FF6B00]" />Other attachments <span className="font-mono text-xs text-slate-500">{others.length}</span></div>
      <div className="space-y-2" data-testid="attachment-list">
        {!others.length && <p className="text-sm text-slate-500">No files or links yet.</p>}
        {others.map((a) => <AttachmentItem key={a.id} task={task} a={a} />)}
      </div>
      {canEdit && (
        <form onSubmit={submit} className="mt-5 space-y-2 border-t border-[#282E3D] pt-5">
          <label className={`${btnGhost} w-full cursor-pointer justify-start`}>
            <Upload className="h-4 w-4" /><span className="truncate" data-testid="task-attachment-selected">{files.length ? (files.length === 1 ? files[0].name : `${files.length} files selected`) : "Choose files (max 25MB each)"}</span>
            <input ref={ref} type="file" multiple className="hidden" data-testid="task-attachment-upload-input" onChange={(e) => setFiles(Array.from(e.target.files))} />
          </label>
          <input placeholder="Link (optional) — https://…" data-testid="task-attachment-link-input" className={inputCls} value={link} onChange={(e) => setLink(e.target.value)} />
          <input placeholder="Note (optional)" data-testid="task-attachment-note-input" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
          <button data-testid="task-attachment-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Uploading…" : "Add attachment"}</button>
        </form>
      )}
    </Panel>
  );
};

const Actions = ({ task, user, onChange }) => {
  const [comment, setComment] = useState("");
  const [voice, setVoice] = useState([]);
  const [outFiles, setOutFiles] = useState([]);
  const [outLink, setOutLink] = useState("");
  const [busy, setBusy] = useState(false);
  const outRef = useRef();
  const step = task.steps[task.current_step];
  const mine = task.status === "in_progress" && (task.assignee_id === user.id || user.is_admin);
  const canPick = task.status === "queued" && (user.is_admin || user.roles.includes(step.role));
  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      onChange((await api.post(`/tasks/${task.id}/${path}`, body)).data);
      setComment("");
      voice.forEach((n) => URL.revokeObjectURL(n.url));
      setVoice([]);
      setOutFiles([]); setOutLink("");
      if (outRef.current) outRef.current.value = "";
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const isLast = task.current_step === task.steps.length - 1;
  const complete = () => {
    if (!step.requires_file && !outFiles.length && !outLink.trim()) return toast.error(`Upload your finished file (or add a link) for "${step.name}" first`);
    const fd = new FormData();
    fd.append("comment", comment);
    fd.append("link", outLink);
    outFiles.forEach((f) => fd.append("files", f));
    act("complete", fd, isLast ? "Work item completed" : step.requires_file ? "Step done — sent to the next queue" : "Step done — your file was handed to the next step");
  };
  const sendBack = () => (comment.trim() || voice.length
    ? act("send-back", feedbackForm(comment, voice), "Sent back with your feedback")
    : toast.error("Add written feedback or a voice note explaining what to fix"));
  if (task.status === "completed") return <Panel className="border-emerald-500/30 p-6 text-emerald-300" data-testid="task-completed-banner"><CheckCircle2 className="mb-2 h-5 w-5" />All steps complete.</Panel>;
  return (
    <Panel className="p-6">
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Current step {task.current_step + 1}/{task.steps.length}</div>
      <div className="mt-1 font-heading text-xl font-bold">{step.name}</div>
      <div className="mt-2 flex items-center gap-2"><RoleBadge role={step.role} />{task.assignee_name && <span className="text-xs text-slate-400" data-testid="task-assignee">with {task.assignee_name}</span>}</div>
      {step.requires_file && <p className="mt-3 rounded-md border border-purple-500/30 bg-purple-500/10 px-3 py-2 text-xs text-purple-200">This step can be completed once faculty, a manager or the super admin has uploaded the batch-annotated files.</p>}
      {canPick && <button data-testid="task-pickup-button" disabled={busy} onClick={() => act("pickup", {}, "Picked up")} className={`${btnPrimary} mt-5 w-full`}><Hand className="h-4 w-4" />Pick up</button>}
      {task.status === "queued" && !canPick && <p className="mt-4 text-sm text-slate-500">Waiting in the {step.role.replace("_", " ")} queue.</p>}
      {task.status === "in_progress" && !mine && <p className="mt-4 text-sm text-slate-500">Being worked on by {task.assignee_name}.</p>}
      {mine && (
        <div className="mt-5 space-y-3">
          <textarea rows={3} data-testid="task-comment-input" placeholder="Comments / feedback (needed to send back: text or voice)" className={inputCls} value={comment} onChange={(e) => setComment(e.target.value)} />
          {task.current_step > (task.start_step || 0) && <VoiceRecorder notes={voice} setNotes={setVoice} prefix="sendback-voice" />}
          {!step.requires_file && (
            <div className="space-y-2 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-3" data-testid="step-output-form">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-300"><PackageCheck className="h-3.5 w-3.5" />Your finished file for "{step.name}" (required)</div>
              <label className={`${btnGhost} w-full cursor-pointer justify-start py-2`}>
                <Upload className="h-4 w-4" /><span className="truncate" data-testid="step-output-selected">{outFiles.length ? (outFiles.length === 1 ? outFiles[0].name : `${outFiles.length} files selected`) : "Choose finished file(s), max 25MB each"}</span>
                <input ref={outRef} type="file" multiple className="hidden" data-testid="step-output-file-input" onChange={(e) => setOutFiles(Array.from(e.target.files))} />
              </label>
              <input placeholder="…or a link to it (optional), https://…" data-testid="step-output-link-input" className={`${inputCls} py-2`} value={outLink} onChange={(e) => setOutLink(e.target.value)} />
            </div>
          )}
          <button data-testid="task-complete-step-button" disabled={busy || voice.length > 0} title={voice.length ? "Voice notes go with Send back. Remove them to complete the step." : undefined} onClick={complete} className={`${btnPrimary} w-full`}><CheckCircle2 className="h-4 w-4" />{busy ? "Uploading…" : `${step.requires_file ? "" : "Upload & "}${isLast ? "complete work item" : "mark step done"}`.replace(/^./, (c) => c.toUpperCase())}</button>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="task-send-back-button" disabled={busy || task.current_step <= (task.start_step || 0)} onClick={sendBack} className={`${btnGhost} hover:border-rose-500/60 hover:text-rose-300`}><CornerUpLeft className="h-4 w-4" />Send back</button>
            <button data-testid="task-release-button" disabled={busy} onClick={() => act("release", {}, "Returned to queue")} className={btnGhost}><Undo2 className="h-4 w-4" />Release</button>
          </div>
        </div>
      )}
    </Panel>
  );
};

const CommentBox = ({ task, onChange }) => <FeedbackComposer task={task} onChange={onChange} />;

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  const load = useCallback(() => api.get(`/tasks/${id}`).then((r) => setTask(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!task) return <div className="text-slate-500">Loading…</div>;
  const mine = task.status === "in_progress" && (task.assignee_id === user.id || user.is_admin);
  const del = async () => {
    if (!window.confirm(`Delete task "${task.title}"? Its files, comments and history will be removed. This can't be undone.`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      nav("/tasks");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="animate-rise">
      <div className="mb-6 flex items-center justify-between">
        <Link to={-1} onClick={(e) => { e.preventDefault(); window.history.back(); }} data-testid="task-back-link" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-white"><ArrowLeft className="h-4 w-4" />Back</Link>
        {isManager(user) && <button data-testid="task-delete-button" onClick={del} className={`${btnGhost} hover:border-rose-500/60 hover:text-rose-300`}><Trash2 className="h-4 w-4" />Delete task</button>}
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2"><TypeBadge type={task.content_type} />{task.class_linked && <ClassLinkedBadge />}<StatusBadge status={task.status} testId="task-status" /></div>
      <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="task-title">{task.title}</h1>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-400">
        <span>Process: <b className="text-slate-200">{task.process_name}</b></span>
        {task.class_name && <span>Class: <b className="text-slate-200">{task.class_name}</b></span>}
        {task.due_date && (() => {
          const overdue = task.status !== "completed" && task.due_date < new Date().toISOString().slice(0, 10);
          return <span data-testid="task-deadline" className={`inline-flex items-center gap-1 ${overdue ? "font-semibold text-rose-300" : ""}`}><CalendarClock className="h-3.5 w-3.5" />Deadline: <b className={overdue ? "" : "text-slate-200"}>{fmtDay(task.due_date)}{overdue && " (overdue)"}</b></span>;
        })()}
        <span>Assigned by {task.created_by}{task.created_by_role === "manager" ? " (Manager)" : task.created_by_role === "super_admin" ? " (Super Admin)" : ""} · {fmtDate(task.created_at)}</span>
      </div>
      {task.description && <p className="mt-4 max-w-3xl whitespace-pre-wrap text-slate-300">{task.description}</p>}
      <div className="my-8"><Stepper task={task} /></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-4">
          <FeedbackToAddress task={task} />
          <StepOutputs task={task} />
          <Actions task={task} user={user} onChange={setTask} />
          <AnnotatedFiles task={task} user={user} onChange={setTask} />
          <Attachments task={task} canEdit={mine} onChange={setTask} />
        </div>
        <Panel className="p-6 lg:col-span-8">
          <div className="mb-5 font-heading text-lg font-semibold">Feedback & full history</div>
          <CommentBox task={task} onChange={setTask} />
          <Timeline task={task} />
        </Panel>
      </div>
    </div>
  );
}
